import React, { useEffect, useState, useMemo } from 'react';
import { View, ScrollView, Platform, Image, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CHANGE_LANGUAGE } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import DefaultScreen from '@components/Global/DefaultScreenView';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import DropDownPicker from 'react-native-dropdown-picker';
import lang from '@components/modals/LanguageModal/constant/languageList';
import i18n from 'i18n.config';
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useWindowDimensions } from 'react-native';
import { XStack, YStack, Card, H5, Separator, SizableText } from 'tamagui';
import { AntDesign } from '@expo/vector-icons';
import { getModernCardStyle, getModernTextColor } from '@stylesheet';
import ErrorBoundary from '@utils/ErrorBoundary';
import { GB, DE, ES, GR, KR, JP, TR, AR, CN, IT, SL } from '@assets/--native/index';

// Safe flag renderer component to avoid property configurable errors
const FlagRenderer = ({ flagCode }: { flagCode: string }) => {
  const flagSize = 25;

  const getFlagSource = () => {
    try {
      switch (flagCode) {
        case 'GB':
          return require('@assets/--native/Flags/great-britain-circular.svg');
        case 'FR':
          return require('@assets/--native/Flags/france-circular.svg');
        case 'DE':
          return require('@assets/--native/Flags/germany-circular.svg');
        case 'ES':
          return require('@assets/--native/Flags/spain-circular.svg');
        case 'JP':
          return require('@assets/--native/Flags/japan-circular.svg');
        case 'TR':
          return require('@assets/--native/Flags/turkey-circular.svg');
        case 'CN':
          return require('@assets/--native/Flags/china-circular.svg');
        case 'IT':
          return require('@assets/--native/Flags/italy-circular.svg');
        case 'GR':
          // Greek flag - use greece-circular.svg (matching Langauge-popover.tsx pattern)
          return require('@assets/--native/Flags/greece-circular.svg');
        case 'UAE':
          // Arabic flag - use united-arab-emirates-circular.svg (matching Langauge-popover.tsx pattern)
          return require('@assets/--native/Flags/united-arab-emirates-circular.svg');
        case 'KR':
          // Korean flag - use south-korea-circular.svg (matching Langauge-popover.tsx pattern)
          return require('@assets/--native/Flags/south-korea-circular.svg');
        case 'SK':
          // Sinhala flag - fallback to SVG component if no image available
          return null;
        default:
          return null;
      }
    } catch (error) {
      console.warn(`Flag image not found for ${flagCode}:`, error);
      return null;
    }
  };

  const getSvgComponent = () => {
    // Only use SVG components for flags without image files (like Sinhala)
    switch (flagCode) {
      case 'SK':
        // Sinhala - use SL component (matching Langauge-popover.tsx)
        return <SL style={[styles.flagContainer, { width: flagSize, height: flagSize }]} />;
      default:
        return null;
    }
  };

  const imageSource = getFlagSource();
  const svgComponent = getSvgComponent();

  if (imageSource) {
    return (
      <Image
        source={imageSource}
        style={{ width: flagSize, height: flagSize }}
        resizeMode="contain"
        onError={(error) => {
          console.warn(`Failed to load flag image for ${flagCode}:`, error);
        }}
      />
    );
  }

  if (svgComponent) {
    return (
      <View style={[styles.flagContainer, { width: flagSize, height: flagSize, justifyContent: 'center', alignItems: 'center' }]}>
        {svgComponent}
      </View>
    );
  }

  // Fallback placeholder
  return (
    <View style={{
      width: flagSize,
      height: flagSize,
      backgroundColor: 'rgba(0,0,0,0.1)',
      borderRadius: flagSize / 2,
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <AntDesign name="question" size={flagSize * 0.6} color="rgba(0,0,0,0.3)" />
    </View>
  );
};

const LanguageScreen = ({ navigation }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();

  // Get current language from Redux or i18n
  const appLanguage = useSelector((state: DefaultRootState) => state.app.language);
  const [currentLanguage, setCurrentLanguage] = useState(
    appLanguage?.value || appLanguage || i18nInstance.language || 'en-GB'
  );
  const [langOpen, setLangOpen] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);

  // Detect initial language on mount
  useEffect(() => {
    // Try to get language from Redux state first
    const reduxLang = appLanguage?.value || appLanguage;

    if (reduxLang) {
      setCurrentLanguage(reduxLang);
      setDetectedLanguage(reduxLang);
    } else {
      // Fallback to i18n language
      const i18nLang = i18nInstance.language;
      if (i18nLang) {
        setCurrentLanguage(i18nLang);
        setDetectedLanguage(i18nLang);
      } else {
        // Detect from browser/system
        if (Platform.OS === 'web') {
          const browserLang = navigator.language || (navigator as any).userLanguage;
          const matchedLang = lang.find(l =>
            l.value === browserLang || l.languageCode === browserLang.split('-')[0]
          );
          if (matchedLang) {
            setCurrentLanguage(matchedLang.value);
            setDetectedLanguage(matchedLang.value);
          } else {
            setCurrentLanguage('en-GB');
            setDetectedLanguage('en-GB');
          }
        } else {
          // For native, use i18n's detected language or default
          const detected = i18nInstance.language || 'en-GB';
          setCurrentLanguage(detected);
          setDetectedLanguage(detected);
        }
      }
    }
  }, []);

  // Update language when Redux state changes
  useEffect(() => {
    const reduxLang = appLanguage?.value || appLanguage;
    if (reduxLang && reduxLang !== currentLanguage) {
      setCurrentLanguage(reduxLang);
    }
  }, [appLanguage]);

  const handleLanguageChange = async (value: string) => {
    setCurrentLanguage(value);
    await i18nInstance.changeLanguage(value);
    dispatch({ type: CHANGE_LANGUAGE, payload: value });
  };

  const currentLangData = lang.find(l => l.value === currentLanguage) || lang[0];
  const textColor = getModernTextColor(theme, 'primary');
  const cardStyle = getModernCardStyle(theme, 'medium');

  // Transform lang array for DropDownPicker (remove React elements to avoid configurable property errors)
  const langItems = useMemo(() => {
    return lang.map(l => ({
      label: l.label,
      value: l.value,
    }));
  }, []);

  return (
    <DefaultScreen
      backButton
      screenTitle={t('changeLang') || 'Language Settings'}
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, minWidth: '100%' }}
        contentContainerStyle={{ gap: 20, paddingBottom: 50 }}
      >
        <ErrorBoundary>
          <Card
            backgroundColor={theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)'}
            padding={20}
            marginBottom={10}
            borderRadius={24}
            elevation={8}
          >
            <YStack gap={16}>
              <H5 color={textColor} marginBottom={8}>
                {t('changeLang') || 'Select Language'}
              </H5>
              <Separator borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />
              <SizableText
                size="$4"
                color={getModernTextColor(theme, 'secondary')}
                marginBottom={12}
              >
                {t('changeLangSub') || 'Choose your preferred language for the application interface.'}
              </SizableText>

              {detectedLanguage && (
                <XStack
                  gap={8}
                  alignItems="center"
                  padding={12}
                  borderRadius={12}
                  backgroundColor={theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)'}
                >
                  <AntDesign
                    name="info-circle"
                    size={16}
                    color={getModernTextColor(theme, 'primary')}
                  />
                  <SizableText
                    size="$3"
                    color={getModernTextColor(theme, 'secondary')}
                    flex={1}
                  >
                    {t('detectedLanguage') || 'System Language'}: {lang.find(l => l.value === detectedLanguage)?.label || detectedLanguage}
                  </SizableText>
                </XStack>
              )}
            </YStack>
          </Card>
        </ErrorBoundary>
        <ErrorBoundary>
          <Card
            backgroundColor={theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)'}
            padding={20}
            borderRadius={24}
            elevation={8}
          >
            <YStack gap={12}>
              <H5 color={textColor} marginBottom={8}>
                {t('availableLanguages') || 'Available Languages'}
              </H5>
              <Separator borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />
              <ErrorBoundary>
                <YStack gap={8}>
                  {lang.map((language) => {
                    const isSelected = language.value === currentLanguage;
                    return (
                      <Pressable
                        key={language.value}
                        onPress={() => handleLanguageChange(language.value)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 16,
                          borderRadius: 12,
                          backgroundColor: isSelected
                            ? (theme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.25)')
                            : (theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)'),
                          borderWidth: isSelected ? 2 : 1,
                          borderColor: isSelected
                            ? (theme ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.6)')
                            : (theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'),
                        }}
                      >
                        {/* <View style={{ marginRight: 12, width: 25, height: 25, justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                          <ErrorBoundary>
                            <FlagRenderer flagCode={language.flag} />
                          </ErrorBoundary>
                        </View> */}
                        <SizableText
                          size="$4"
                          flex={1}
                          color={textColor}
                          fontWeight={isSelected ? '600' : '400'}
                        >
                          {language.label}
                        </SizableText>
                        {isSelected && (
                          <AntDesign
                            name="check-circle"
                            size={20}
                            color={theme ? '#3b82f6' : '#60a5fa'}
                          />
                        )}
                      </Pressable>
                    );
                  })}
                </YStack>
              </ErrorBoundary>
            </YStack>
          </Card>
        </ErrorBoundary>
      </ScrollView>
    </DefaultScreen>
  );
};

export default LanguageScreen;

